/**
 * @file Store-only helpers for {@link useRovoConfigStore}.
 *
 * Map a connector identifier from the **config namespace** (what `useRovoConfig`
 * emits — `ProductResourceTypes3P` connector keys for 3P connectors, and 1P
 * product / noun keys for first-party products) to the **chat / knowledge
 * namespace** that downstream `@atlassian/conversation-assistant-store`
 * selectors key off (the `source` field returned by the knowledge endpoint).
 *
 * Internal to the store — not exported via a package subpath.
 *
 * ---------------------------------------------------------------------------
 * RAOC-23 — Why this exists
 * ---------------------------------------------------------------------------
 * On each side of the `rovo_cas_use_rovo_config` feature gate the connector
 * lists come from a different backend, and those backends return **different
 * key strings for the same connector**:
 *   - FG off → the legacy knowledge endpoint (conversational-ai-platform),
 *     which returns chat-namespace `source` strings such as `google_drive`,
 *     `microsoft_sharepoint`, `jsm`, `google_drive_sl`.
 *   - FG on  → the combined-config endpoint (via `useRovoConfig`), which
 *     returns config-namespace keys: a kebab-case product key (`drive`) plus a
 *     connector `resourceType` (`google-drive-connector`).
 * We treat the FG-on (combined-config) backend as the source of truth and
 * remap its identifiers here so that consumers of `useRovoConfigStore` always
 * see the chat-namespace `source` they were built against, regardless of the
 * gate.
 *
 * ---------------------------------------------------------------------------
 * Why keying on `resourceType` (not the product key) for 3P
 * ---------------------------------------------------------------------------
 * The kebab product key alone is **ambiguous**: a single product (e.g. config
 * `drive`) can correspond to two different knowledge `source` values depending
 * on whether the connected datasource is the full connector (`google_drive`)
 * or the SmartLinks connector (`google_drive_sl`). The only field that
 * disambiguates is the connector key — which the config response carries as
 * `resourceType` (e.g. `google-drive-connector` vs `smartlinks-connector-gdrive`),
 * matching the BE `KnowledgeSourceType.thirdPartyConnectorKey` 1:1. So 3P
 * remapping is keyed on `resourceType`; only the 1P / noun entries (which have
 * no `resourceType`) are remapped by product key.
 *
 * ---------------------------------------------------------------------------
 * Provenance
 * ---------------------------------------------------------------------------
 * Both maps below are generated from the authoritative
 * `KnowledgeSourceType` enum in conversational-ai-platform
 * (`.../platform/base/knowledge/KnowledgeSourceType.kt`): the RHS is the enum
 * `value` (`@JsonValue` = the `source` the knowledge endpoint emits) and the
 * LHS is the enum `thirdPartyConnectorKey` (3P) / config `product` (1P/noun).
 * The FG-off store keys connectors verbatim by this `source`
 * (`connectors[source.source] = ...` in
 * `conversation-assistant-store/.../connectors-store`), so it is the only
 * source of truth for the key strings.
 *
 * TODO RAOC-23: once the chat selector hooks are fully migrated and the chat
 * backend converges on the config-namespace keys, these remaps can be deleted.
 * Alternatively: converge to this version for search as well.
 */

/**
 * Maps a connector `resourceType` (config-namespace connector key, equal to
 * `ProductResourceTypes3P` values and the BE `thirdPartyConnectorKey`) to the
 * chat-namespace `source` value.
 *
 * Generated from `KnowledgeSourceType` (conversational-ai-platform). Covers
 * every 3P knowledge source, including the SmartLinks (`*_sl`) variants which
 * share a product key with their full-connector sibling but have a distinct
 * `resourceType` and a distinct `source`.
 *
 * NOTE: `forge-connector` intentionally omitted. The backend reuses the
 * generic `forge-connector` key for multiple Forge-hosted integrations, so
 * it cannot be mapped to a single `source` unambiguously. Until the backend
 * disambiguates it, Forge connectors fall through to the product-key remap
 * (and otherwise pass through unchanged) rather than silently resolving to
 * the wrong source.
 *
 * TODO RAOC-23: Move this constant into the 3P static configs per connector
 */
export const SOURCE_BY_RESOURCE_TYPE: Readonly<Record<string, string>> = {
	'adobe-sign-connector': 'adobe_sign',
	'adobexd-connector': 'adobe_xd',
	'aha-connector': 'aha',
	'airtable-connector': 'airtable',
	'asana-connector': 'asana',
	'aws-connector': 'aws',
	'azure-connector': 'azure',
	'azure-devops-connector': 'azure_devops',
	'bitbucket-connector': 'bitbucket',
	'box-connector': 'box',
	'canva-connector': 'canva',
	'clickup-connector': 'clickup',
	'confluence-dc-connector': 'confluence-dc',
	'confluent-connector': 'confluent',
	'databricks-connector': 'databricks',
	'datadog-connector': 'datadog',
	'docker-connector': 'docker',
	'docusign-connector': 'docusign',
	'dovetail-connector': 'dovetail',
	'dropbox-connector': 'dropbox',
	'entra-id-connector': 'entra',
	'figma-connector': 'figma',
	'freshservice-connector': 'freshservice',
	'github-connector': 'github',
	'gitlab-connector': 'gitlab',
	'gmail-connector': 'gmail',
	'google-calendar-connector': 'google_calendar',
	'google-cloud-platform-connector': 'gcp',
	'google-drive-connector': 'google_drive',
	'hubspot-connector': 'hubspot',
	'jenkins-connector': 'jenkins',
	'jira-dc-connector': 'jira_dc',
	'launchdarkly-connector': 'launchdarkly',
	'loom-connector': 'loom',
	'lucid-connector': 'lucid',
	'microsoft-connector': 'microsoft',
	'microsoft-teams-connector': 'teams',
	'miro-connector': 'miro',
	'monday-connector': 'monday',
	'mural-connector': 'mural',
	'notion-connector': 'notion',
	'onedrive-connector': 'microsoft_onedrive',
	'outlook-calendar-connector': 'microsoft_outlook_calendar',
	'outlook-mail-connector': 'microsoft_outlook_mail',
	'pagerduty-connector': 'pagerduty',
	'pipedrive-connector': 'pipedrive',
	'salesforce-connector': 'salesforce',
	'sentry-connector': 'sentry',
	'servicenow-connector': 'servicenow',
	'sharepoint-connector': 'microsoft_sharepoint',
	'slack-connector': 'slack',
	'smartlinks-connector-adobexd': 'adobe_xd_sl',
	'smartlinks-connector-airtable': 'airtable_sl',
	'smartlinks-connector-amplitude': 'amplitude',
	'smartlinks-connector-asana': 'asana_sl',
	'smartlinks-connector-box': 'box_sl',
	'smartlinks-connector-clickup': 'clickup_sl',
	'smartlinks-connector-docusign': 'docusign_sl',
	'smartlinks-connector-dovetail': 'dovetail_sl',
	'smartlinks-connector-dropbox': 'dropbox_sl',
	'smartlinks-connector-figma': 'figma_sl',
	'smartlinks-connector-gdrive': 'google_drive_sl',
	'smartlinks-connector-gitlab': 'gitlab_sl',
	'smartlinks-connector-hubspot': 'hubspot_sl',
	'smartlinks-connector-launchdarkly': 'launchdarkly_sl',
	'smartlinks-connector-lucid': 'lucid_sl',
	'smartlinks-connector-miro': 'miro_sl',
	'smartlinks-connector-mural': 'mural_sl',
	'smartlinks-connector-notion': 'notion_sl',
	'smartlinks-connector-onedrive': 'microsoft_onedrive_sl',
	'smartlinks-connector-pagerduty': 'pagerduty_sl',
	'smartlinks-connector-pipedrive': 'pipedrive_sl',
	'smartlinks-connector-power-bi': 'microsoft_power_bi',
	'smartlinks-connector-salesforce': 'salesforce_sl',
	'smartlinks-connector-sentry': 'sentry_sl',
	'smartlinks-connector-sharepoint': 'microsoft_sharepoint_sl',
	'smartlinks-connector-slack': 'slack_sl',
	'smartlinks-connector-smartsheet': 'smartsheet_sl',
	'smartlinks-connector-stripe': 'stripe_sl',
	'smartlinks-connector-teams': 'microsoft_teams_sl',
	'smartlinks-connector-todoist': 'todoist',
	'smartlinks-connector-webex': 'cisco_webex',
	'smartlinks-connector-zeplin': 'zeplin',
	'smartsheet-connector': 'smartsheet',
	'splunk-connector': 'splunk',
	'stripe-connector': 'stripe',
	'webcrawler-connector': 'atlassian-web-crawler',
	'zendesk-connector': 'zendesk',
};

/**
 * Maps a first-party / noun **product key** (config-namespace, as it appears in
 * `accessibleProducts`) to the chat-namespace `source` value, for the entries
 * where the two namespaces differ. 1P/noun products have no `resourceType`, so
 * they are remapped by product key. Everything not listed (e.g. `confluence`,
 * `jira`, `compass`, `trello`) is identical across both namespaces and passes
 * through unchanged.
 *
 * Generated from the 1P / federated entries of `KnowledgeSourceType`.
 */
export const SOURCE_BY_PRODUCT_KEY: Readonly<Record<string, string>> = {
	'jira-servicedesk': 'jsm',
	goal: 'atlas_goal',
	project: 'atlas_project',
};

/**
 * Translate a 3P connector `resourceType` into its chat-namespace `source`.
 * Returns `undefined` when the resourceType is unknown / absent, so callers can
 * fall back to {@link toChatSourceKey}.
 *
 * @see {@link SOURCE_BY_RESOURCE_TYPE}
 */
export const sourceFromResourceType = (
	resourceType: string | null | undefined,
): string | undefined => (resourceType ? SOURCE_BY_RESOURCE_TYPE[resourceType] : undefined);

/**
 * Translate a single config-namespace **product key** into the chat-namespace
 * `source`. Returns the input unchanged when no remapping is required.
 *
 * Used for 1P / noun products (which have no `resourceType`) and as the
 * fallback for 3P connectors whose `resourceType` is unknown / absent.
 *
 * @see {@link SOURCE_BY_PRODUCT_KEY}
 */
export const toChatSourceKey = (productKey: string): string =>
	SOURCE_BY_PRODUCT_KEY[productKey] ?? productKey;

/**
 * Resolve the chat-namespace `source` for a connector, preferring the
 * unambiguous `resourceType` join (3P) and falling back to the product-key
 * remap (1P / noun, or 3P connectors without a known `resourceType`).
 *
 * @param productKey   config-namespace product key (kebab-case)
 * @param resourceType config-namespace connector key, when available
 */
export const resolveChatSource = (productKey: string, resourceType?: string | null): string =>
	sourceFromResourceType(resourceType) ?? toChatSourceKey(productKey);
